import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Rating,
  Pagination,
  CircularProgress,
  Container
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Edit,
  Business,
  Phone,
  Email,
  Inventory,
  Analytics,
  Folder,
  Payment,
  Chat,
  AccountBalance,
  SwapHoriz
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
  });

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuppliers();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await suppliersAPI.getAll(params);
      const { suppliers: suppliersData, pagination: paginationData } =
        response.data.data;

      setSuppliers(suppliersData);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching suppliers", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, supplier) => {
    setAnchorEl(event.currentTarget);
    setSelectedSupplier(supplier);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSupplier(null);
  };

  const handleView = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/edit`);
    }
    handleMenuClose();
  };

  const handleSupplierProducts = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/products`);
    }
    handleMenuClose();
  };

  const handleSupplierAnalytics = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/analytics`);
    }
    handleMenuClose();
  };

  const handleSupplierDocuments = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/documents`);
    }
    handleMenuClose();
  };

  const handleSupplierPayments = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/payments`);
    }
    handleMenuClose();
  };

  const handleSupplierCommunications = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/communications`);
    }
    handleMenuClose();
  };

  const handleSupplierFinancials = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/financials`);
    }
    handleMenuClose();
  };

  const handleSupplierReturns = () => {
    if (selectedSupplier) {
      navigate(`/hospital/pharmacy/suppliers/${selectedSupplier._id}/returns`);
    }
    handleMenuClose();
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const getPaymentTermsColor = (terms) => {
    const colors = {
      advance: "success",
      "7_days": "info",
      "15_days": "primary",
      "30_days": "warning",
      "45_days": "warning",
      "60_days": "error",
    };
    return colors[terms] || "default";
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h3" fontWeight="bold">Suppliers</Typography>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <Button 
              variant="contained" 
              startIcon={<Add />} 
              onClick={() => navigate("/hospital/pharmacy/suppliers/new")} 
              sx={{ 
                borderRadius: 2, 
                boxShadow: 2, 
                transition: "0.3s", 
                "&:hover": { transform: "scale(1.03)" } 
              }}
            >
              Add Supplier
            </Button>
          )}
        </Box>

        {/* Search Card */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search suppliers by name, contact person, or email..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" color="text.secondary" align="center">
                  Total: {pagination.total} suppliers
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Suppliers Grid */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {suppliers.map((supplier) => (
                <Grid item xs={12} sm={6} md={4} key={supplier._id}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      boxShadow: 3,
                      transition: "0.3s",
                      "&:hover": { 
                        boxShadow: 6, 
                        transform: "translateY(-5px)" 
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              width: 56,
                              height: 56,
                            }}
                          >
                            {getInitials(supplier.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="h5" fontWeight="bold">
                              {supplier.name}
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              {supplier.contactPerson}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, supplier)}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Phone fontSize="small" color="action" />
                          <Typography variant="h6">
                            {supplier.phone}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Email fontSize="small" color="action" />
                          <Typography variant="h6">
                            {supplier.email}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ 
                          display: "flex", 
                          gap: 1, 
                          flexWrap: "wrap", 
                          mb: 2 
                        }}
                      >
                        <Chip
                          label={supplier.paymentTerms ? supplier.paymentTerms.replace("_", " ") : "N/A"}
                          size="small"
                          color={getPaymentTermsColor(supplier.paymentTerms)}
                          sx={{ borderRadius: 2 }}
                        />
                        <Chip
                          label={supplier.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={supplier.isActive ? "success" : "default"}
                          variant={supplier.isActive ? "filled" : "outlined"}
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="h6" color="text.secondary">
                            Lead Time
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {supplier.leadTime} days
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="h6" color="text.secondary">
                            Products
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {supplier.productCount ?? 0}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Rating 
                          value={supplier.rating || 0} 
                          size="small" 
                          readOnly 
                        />
                        {supplier.drugLicenseNumber && (
                          <Typography variant="body1" color="text.secondary">
                            License: {supplier.drugLicenseNumber}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* No Results */}
            {suppliers.length === 0 && !loading && (
              <Card sx={{ textAlign: "center", py: 6, borderRadius: 3, boxShadow: 2, mt: 4 }}>
                <CardContent>
                  <Business
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No suppliers found
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {searchTerm
                      ? "Try adjusting your search terms"
                      : "Get started by adding your first supplier"}
                  </Typography>
                  {hasAnyRole(["admin", "inventory_manager"]) && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate("/hospital/pharmacy/suppliers/new")}
                      sx={{ borderRadius: 2 }}
                    >
                      Add First Supplier
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Supplier Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              width: 220,
              maxHeight: 400,
            },
          }}
        >
          <MenuItem onClick={handleView}>
            <Visibility sx={{ mr: 1, fontSize: 20 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={handleSupplierFinancials}>
            <AccountBalance sx={{ mr: 1, fontSize: 20 }} />
            Financials
          </MenuItem>

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem onClick={handleEdit}>
              <Edit sx={{ mr: 1, fontSize: 20 }} />
              Edit Supplier
            </MenuItem>
          )}

          <MenuItem onClick={handleSupplierProducts}>
            <Inventory sx={{ mr: 1, fontSize: 20 }} />
            Products
          </MenuItem>
          <MenuItem onClick={handleSupplierReturns}>
            <SwapHoriz sx={{ mr: 1, fontSize: 20 }} />
            Returns
          </MenuItem>

          <MenuItem onClick={handleSupplierAnalytics}>
            <Analytics sx={{ mr: 1, fontSize: 20 }} />
            Analytics
          </MenuItem>

          <MenuItem onClick={handleSupplierCommunications}>
            <Chat sx={{ mr: 1, fontSize: 20 }} />
            Communications
          </MenuItem>

          <MenuItem onClick={handleSupplierDocuments}>
            <Folder sx={{ mr: 1, fontSize: 20 }} />
            Documents
          </MenuItem>

          <MenuItem onClick={handleSupplierPayments}>
            <Payment sx={{ mr: 1, fontSize: 20 }} />
            Payments
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};